import { ReportScope } from './collaborators';
import { validatePhone } from '@utils/formValidation/phoneValidation';
import { validateCNPJ } from '@utils/validateCnpj';
import { validateCPF } from '@utils/validateCpf';
import { cleanDocument } from '@utils/inputValidation';
import { z } from 'zod';
import { VibError } from './vibError';
import { DashboardVersion } from './dashboardVersion';
import { Attribution } from '@utils/captureAttribution';
import { FirstTouch } from '@utils/captureFirstTouch';

export type AuthResponse = { accessToken: string } | VibError;

export const signInCredentialsSchema = z.object({
  document: z.preprocess(
    (value) => cleanDocument(value),
    z
      .string()
      .trim()
      .nonempty({
        message: 'O documento é obrigatório',
      })
      .refine(
        (value) => {
          if (value.length === 11) return validateCPF(value);
          if (value.length === 14) return validateCNPJ(value);
          return false;
        },
        { message: 'O formato do documento é inválido' },
      ),
  ),
  password: z.string().trim().nonempty({
    message: 'A senha é obrigatória!',
  }),
});
export type SignInCredentials = z.infer<typeof signInCredentialsSchema>;

export const resetPasswordSchema = signInCredentialsSchema.pick({
  document: true,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const registerSchema = z.object({
  name: z.string().trim().nonempty({
    message: 'O nome é obrigatório',
  }),
  email: z
    .string()
    .trim()
    .nonempty({
      message: 'O email é obrigatório',
    })
    .email({
      message: 'O formato do email é invalido',
    }),
  phone: validatePhone(),
  termsOfUse: z
    .boolean()
    .refine((value) => value === true, {
      message: 'Você deve aceitar os termos de uso para continuar',
    })
    .default(false),
  birthDate: z
    .date({
      errorMap: (issue) => {
        switch (issue.code) {
          case 'invalid_type':
            return { message: 'A data de nascimento é obrigatória' };
          case 'invalid_date':
            return { message: 'Formato da data é inválido' };
          default:
            return { message: 'Verifique este campo' };
        }
      },
    })
    .refine(
      (value) => {
        const valueDate = new Date(value);
        const now = new Date();

        return now.getTime() - valueDate.getTime() >= 568025136000;
      },
      { message: 'Você deve ser maior de 18 anos para se cadastrar.' },
    )
    .optional(),
  document: z.preprocess(
    (value) => cleanDocument(value),
    z
      .string()
      .trim()
      .nonempty({
        message: 'O documento é obrigatório',
      })
      .refine(
        (value) => {
          if (value.length === 11) return validateCPF(value);
          if (value.length === 14) return validateCNPJ(value);
          return false;
        },
        { message: 'O formato do documento é inválido' },
      ),
  ),
  password: z.string().trim().nonempty({
    message: 'A senha é obrigatória',
  }),
  passwordConfirmation: z.string().trim().nonempty({
    message: 'A confirmação de senha é obrigatória',
  }),
  categoryId: z.string().trim().nonempty({
    message: 'A categoria é obrigatória.',
  }),
  preferredName: z
    .string()
    .max(120, 'O tamanho máximo desse campo é 120 caracteres.')
    .nullable()
    .optional(),
  gender: z
    .string()
    .max(40, 'O tamanho máximo desse campo é 40 caracteres.')
    .nullable()
    .optional(),
  genderFreeText: z
    .string()
    .max(40, 'O tamanho máximo desse campo é 40 caracteres.')
    .optional(),
  audiencePlatform: z.string().optional(),
  audiencePlatformOther: z.string().optional(),
  audienceUsername: z.string().optional(),
  audienceSize: z.string().optional(),
});
export type UserRegisterDto = z.infer<typeof registerSchema> & {
  referrerCode: string | null;
  registerCommissionTracker: string | null;
  /** `eventID` do `Lead` do pixel, para o servidor mandar o mesmo evento (`utils/metaLeadEvent`). */
  metaLeadEventId?: string;
  /** Client id do GA4 (`utils/gaClientId`). */
  gaClientId?: string;
} & Attribution &
  FirstTouch;

export const userInfoSchema = registerSchema.pick({
  name: true,
  email: true,
  phone: true,
  termsOfUse: true,
  preferredName: true,
});
export type UserInfo = z.infer<typeof userInfoSchema>;

export const documentInfoSchema = registerSchema
  .pick({
    document: true,
    birthDate: true,
    categoryId: true,
    gender: true,
    genderFreeText: true,
  })
  .refine((data) => !(data.document.length <= 11 && !data.birthDate), {
    message: 'A data de nascimento é obrigatória',
    path: ['birthDate'],
  });
export type DocumentInfo = z.infer<typeof documentInfoSchema>;

// Códigos gravados pela platform-api (changeset 652); vazio = não informou.
export const audiencePlatformOptions = [
  { value: '', label: 'Não informar' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'onlyfans', label: 'OnlyFans' },
  { value: 'privacy', label: 'Privacy' },
  { value: 'outro', label: 'Outro' },
];
export const audienceSizeOptions = [
  { value: '', label: 'Não informar' },
  { value: 'sem_audiencia', label: 'Ainda não tenho audiência' },
  { value: 'ate_1k', label: 'Até 1 mil' },
  { value: '1k_5k', label: 'De 1 mil a 5 mil' },
  { value: '5k_25k', label: 'De 5 mil a 25 mil' },
  { value: '25k_100k', label: 'De 25 mil a 100 mil' },
  { value: '100k_500k', label: 'De 100 mil a 500 mil' },
  { value: 'mais_500k', label: 'Mais de 500 mil' },
];

export const choosePasswordSchema = registerSchema
  .pick({
    password: true,
    passwordConfirmation: true,
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'As senhas não convergem',
    path: ['passwordConfirmation'], // path of error
  });
export type ChoosePassword = z.infer<typeof choosePasswordSchema>;

export const updatePasswordSchema = registerSchema
  .pick({
    password: true,
  })
  .extend({
    newPassword: z.string().trim().nonempty({
      message: 'A nova senha é obrigatória.',
    }),
    newPasswordConfirmation: z.string().trim().nonempty({
      message: 'A confirmação da nova senha é obrigatória.',
    }),
  })
  .refine((data) => data.password !== data.newPassword, {
    message: 'A nova senha não pode ser igual a antiga.',
    path: ['newPassword'],
  })
  .refine((data) => data.newPassword === data.newPasswordConfirmation, {
    message: 'As senhas não convergem.',
    path: ['newPasswordConfirmation'],
  });
export type UpdatePasswordDto = z.infer<typeof updatePasswordSchema>;

export type UserProduct = {
  id: number;
  name: string;
  active: boolean;
  userId: number;
  productCode: string;
  userRole: UserRole;
  botMonitoring: {
    leftOrKicked: boolean | null;
    enableLeftOrKickedAlert: boolean;
    withoutPermissions: boolean | null;
    enableWithoutPermissionsAlert: boolean;
    withoutCommunication: boolean | null;
    enableWithoutCommunicationAlert: boolean;
  };
};

/**
 * Importado de `collaborators` porque o escopo nasceu lá — o grant é quem o define — e passou a
 * descrever também o alcance de quem tem comissão. Um enum próprio aqui seria o segundo vocabulário
 * para a mesma coisa, livre para divergir do que a API manda.
 */
export type Groups = {
  owner: UserProduct[];
  coproducer: UserProduct[];
  affiliated: UserProduct[];
  referrer: UserProduct[];
  /**
   * Colaboração ATIVA — o único balde que não é vínculo de comissão.
   *
   * Sem ele, quem entra pela Central de Colaboradores existe no banco e é invisível para o app: as
   * quatro listas acima vêm de `commission_rate`/`bot_user`, e colaborador puro não aparece em
   * nenhuma. A comunidade some do seletor e a rota dela cai em `/404` pelo `CurrentProductContext`.
   *
   * Alcance NÃO é papel: `getUserRoleByProduct` continua conhecendo só os quatro, e a migração dos
   * filtros por papel é trabalho próprio (§6.3).
   */
  collaborator: UserProduct[];
};

/**
 * O escopo de um produto: produto inteiro, ou a fatia de qual papel.
 *
 * Vem RESOLVIDO pela `platform-api` (api#277) e não se recalcula aqui: escopo é conclusão de
 * autorização, e é a mesma função do servidor que decide o número do relatório. Reproduzir a regra
 * no browser criaria duas respostas para a mesma pergunta — o erro que esta série já desfez quatro
 * vezes.
 */
export type ProductScope = {
  botConfigId: number;
  scope: ReportScope;
  /** Só em `COMMISSION`: diz QUAL fatia. Ausente em `PRODUCT`. */
  role?: UserRole;
};

/**
 * A resposta de `getGroups`: os baldes MAIS o descritor de escopo.
 *
 * Tipo separado de `Groups`, e não um campo dentro dele, porque `keyof Groups` é usado como "nome de
 * balde": `filterProductIdsByRole` indexa `groups[role]` com a chave minúsculo do papel. Um campo
 * que não fosse `UserProduct[]` ali passaria a ser um `keyof` válido e apontado como balde —
 * descoberto pelo compilador ao tentar a outra forma.
 *
 * `reportScopeByProduct` é opcional porque a resposta pode vir de uma `platform-api` anterior à
 * api#277, na janela de deploy: ausente, o painel se comporta como antes.
 */
export type GroupsResponse = Groups & {
  reportScopeByProduct?: ProductScope[];
};

export type SessionUser = {
  id: number;
  name: string;
  email: string;
  document: string;
  phone: string;
  birthDate: string;
  automaticWithdralEnabled: boolean;
  creationDate: string;
  lastProfileUpdateAlertDate: string | null;
  /** `null` = nunca escolheu. Opcional porque o campo só existe depois do deploy do backend. */
  dashboardVersion?: DashboardVersion | null;
  /** Opcional porque o campo só existe depois do deploy do backend; `undefined` ainda vê o diálogo. */
  welcomeDialogDismissed?: boolean;
};

/**
 * `COLLABORATOR` é ALCANCE, não papel de comissão — e por isso não tem casa no bitmask.
 *
 * A matriz de permissão é uma string de três posições (OWNER, COPRODUCER, AFFILIATED). Colaborador
 * não tem posição ali de propósito: a permissão dele vem do GRANT, e a matriz nunca lhe concede
 * nada. É a mesma decisão que a platform-api toma em `resolveEffectivePermissions`, onde papel vazio
 * significa "só o que foi concedido".
 *
 * Existir como valor é o que impede o resolvedor de papel de cair em `AFFILIATED` para quem só
 * colabora — o default que daria a ele as permissões de afiliado sem grant nenhum.
 */
export type UserRole =
  | 'OWNER'
  | 'COPRODUCER'
  | 'AFFILIATED'
  | 'REFERRER'
  | 'COLLABORATOR';
export const UserRoles: Record<UserRole, UserRole> = {
  OWNER: 'OWNER',
  COPRODUCER: 'COPRODUCER',
  AFFILIATED: 'AFFILIATED',
  COLLABORATOR: 'COLLABORATOR',
  REFERRER: 'REFERRER',
};
export enum ParsedUserRole {
  OWNER = 'Criador',
  COPRODUCER = 'Coprodutor',
  AFFILIATED = 'Afiliado',
  REFERRER = 'Indicação',
  COLLABORATOR = 'Colaborador',
}

/**
 * Narrows a role this app did not produce - a query string off a link, a stale stored value - to one the
 * CALLER accepts, and returns undefined for anything else.
 *
 * The allowed set is a parameter and not the four application roles, because a screen can support fewer:
 * the conversion filters drop REFERRER from their options on purpose, so accepting it there seeds the
 * report with a role its own select cannot show and scopes the communities to referrals. A real role the
 * report does not offer breaks exactly like an invented one; validating against the app rather than
 * against the screen is what lets it through.
 *
 * A report screen seeds its filters from the URL and lets that take precedence over the stored and the
 * default selection, which is the point of a shareable link. A cast instead of a check makes that
 * precedence apply to a value nothing can resolve: the community list filters on a role no community
 * holds and comes back empty, the role select renders with no matching option, and the screen persists
 * the bad value into the shared dashboard namespace on the way past - so the next visit to the dashboard
 * itself starts broken too. Undefined instead falls through to the stored value or the default, the same
 * way an unparseable date in the same link does.
 *
 * Uses the known keys rather than `in`, which would also accept anything on Object's prototype
 * (`?role=constructor`).
 */
export const parseUserRole = (
  value: unknown,
  allowed: readonly UserRole[] = Object.keys(UserRoles) as UserRole[],
): UserRole | undefined =>
  typeof value === 'string' && allowed.includes(value as UserRole)
    ? (value as UserRole)
    : undefined;

export type UserCommunity = {
  id: number;
  name: string;
  active: boolean;
  userId: number;
  productCode: string;
  userRoles: UserRole[];
};
